import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../theme-system/ThemeContext';
import { PROJECTS, BRANDS } from '../home-concepts/shared';

/* The side menu: Home, every project (small picture + what it is), and contact.
   The page you are on is marked. Najdar sits last, as on the home page. */
const WORK = [
  ...PROJECTS.filter((p) => p.id !== 'najdar'),
  ...PROJECTS.filter((p) => p.id === 'najdar')
];

/* A 32px version of the project's brand card: its colour with its logo (or its first letter). */
const BrandTile = ({ id, cover, title }) => {
  const b = BRANDS[id];
  if (!b) return cover ? <img src={cover} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : null;
  if (b.image) return <img src={b.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;
  return (
    <span style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', background: b.bg }}>
      {b.logo
        ? <img src={b.logo} alt="" loading="lazy" style={{ width: '78%', height: '78%', objectFit: 'contain', borderRadius: b.radius || 0, mixBlendMode: b.blend || 'normal' }} />
        : <span style={{ color: b.color, fontWeight: 800, fontSize: '14px', fontFamily: b.font || 'inherit' }}>{title.charAt(0)}</span>}
    </span>
  );
};

const Row = ({ href, active, children }) => (
  <a href={href} style={{
    display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', margin: '0 10px',
    borderRadius: '10px', textDecoration: 'none', color: 'var(--text-primary)',
    background: active ? 'var(--bg-accent)' : 'transparent',
    border: `1px solid ${active ? 'var(--border-color)' : 'transparent'}`
  }}
  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--bg-accent)'; }}
  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
    {children}
  </a>
);

const Label = ({ children }) => (
  <div style={{ padding: '0 22px', margin: '22px 0 8px', fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
    {children}
  </div>
);

const Sidebar = ({ isOpen, isMobile }) => {
  useTheme();
  const { pathname } = useLocation();
  const [workOpen, setWorkOpen] = useState(false);

  return (
    <aside aria-hidden={!isOpen} style={{
      // a floating drawer over the page (it never pushes the content sideways)
      position: 'fixed', top: 76, left: 12, bottom: 12, width: 'min(300px, calc(100vw - 24px))', boxSizing: 'border-box',
      transform: isOpen ? 'none' : 'translateX(calc(-100% - 24px))', willChange: 'transform',
      transition: 'transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1)',
      background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 20,
      boxShadow: '0 24px 60px rgba(0,0,0,0.18)', zIndex: 1001,
      overflowY: 'auto', overscrollBehavior: 'contain', padding: '12px 0 20px'
    }}>
      <nav aria-label="Site">
        <Row href="/" active={pathname === '/'}>
          <span style={{ width: 32, height: 32, borderRadius: 8, display: 'grid', placeItems: 'center', background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 11l8-7 8 7v8a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
          </span>
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Home</span>
        </Row>

        {/* Work stays folded until tapped */}
        <button onClick={() => setWorkOpen((o) => !o)} aria-expanded={workOpen} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: 'calc(100% - 20px)', margin: '14px 10px 6px',
          padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)',
          color: 'var(--text-primary)', cursor: 'pointer', font: 'inherit'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600 }}>Work <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>· {WORK.length}</span></span>
          <span aria-hidden="true" style={{ transform: workOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', fontSize: '12px' }}>▾</span>
        </button>
        {workOpen && WORK.map((p) => (
          <Row key={p.id} href={p.href} active={pathname === p.href}>
            <span style={{ width: 32, height: 32, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
              <BrandTile id={p.id} cover={p.cover} title={p.title} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</span>
              <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.category}</span>
            </span>
          </Row>
        ))}

        <Label>Contact</Label>
        <div style={{ padding: '0 22px', fontSize: '13px', color: 'var(--text-primary)', userSelect: 'all', wordBreak: 'break-all' }}>
          fahadnimer1@gmail.com
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
